import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { DXL_BRANDING } from '@dxl/shared';
import { brandingAnwenden } from '@dxl/ui';
import '@fontsource-variable/montserrat/wght.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@dxl/ui/styles.css';
import './styles/app.css';
import { erzeugeRouter } from './router';
import { meAbfrage, sitzungLeeren } from './lib/sitzung';
import { beiSitzungsende } from './lib/api';
import { ApiFehler } from './lib/api';
import { faviconSetzen, schriftLaden } from './lib/schriften';

/* Nach einer Auslieferung fehlen Skripte des alten Stands: einmal neu laden statt leerer Seite.
   Höchstens alle 30 Sekunden, damit ein echter Fehler nicht in eine Neuladeschleife führt. */
export function neuLadenNachAuslieferung(): boolean {
  try {
    const zuletzt = Number(sessionStorage.getItem('dxl-neu-geladen') ?? 0);
    if (Date.now() - zuletzt < 30_000) return false;
    sessionStorage.setItem('dxl-neu-geladen', String(Date.now()));
  } catch {
    /* ohne Speicher trotzdem einmal neu laden */
  }
  window.location.reload();
  return true;
}
window.addEventListener('vite:preloadError', (e) => {
  if (neuLadenNachAuslieferung()) e.preventDefault();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (n, e) => !(e instanceof ApiFehler && e.status < 500) && n < 2,
      refetchOnWindowFocus: false,
    },
  },
});
const router = erzeugeRouter(queryClient);

beiSitzungsende(() => {
  sitzungLeeren(queryClient);
  void router.invalidate();
});

/** Farben, Schrift und Icon des Marktplatzes (oder DXL-Standard) auf die ganze Oberfläche anwenden */
function Branding() {
  const { data: me } = useQuery(meAbfrage);
  // Plattform (DXL-Admin): gespeichertes Plattform-Branding, sonst DXL-Standard – wie ein Marktplatz sein eigenes
  const b = me?.mandant?.branding ?? me?.plattformBranding ?? DXL_BRANDING;
  useEffect(() => {
    void schriftLaden(b.schrift);
    brandingAnwenden(b);
    faviconSetzen(b.icon);
  }, [b]);
  return null;
}

createRoot(document.getElementById('app')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Branding />
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
